/**
 * The Configuration page's view of the zero-commission promotions applied at payment
 * (BL-02 first contract, BL-03 SOS campaign). The backend prices them in
 * `backend/src/domain/contracts/zeroCommissionPromotions.ts`; this module reads and writes the
 * same stored settings, in the same precedence: Delphi's name, which this page writes (every
 * setting follows Delphi, bridge day check 2026-10-27 H-2), then the English name the page used to
 * write, only while Delphi's row is missing. Both promotions are off until switched on.
 */

export interface ZeroCommissionPromotionFormValues {
    firstContractEnabled: boolean;
    /** Days waived from the start of the client's first contract. */
    firstContractFreeDays: number;
    /** Largest loan (RON) that qualifies. */
    firstContractLoanLimitRon: number;
    /** Contracts signed before this day (YYYY-MM-DD) do not qualify. */
    firstContractStartDate: string | null;
    sosEnabled: boolean;
    sosFromDate: string | null;
    sosToDate: string | null;
}

type SettingKey = keyof ZeroCommissionPromotionFormValues;

/** Delphi's name first, English fallback - kept equal to `PROMOTION_SETTING_NAMES` on the backend. */
const SETTING_NAMES: Readonly<Record<SettingKey, readonly [delphi: string, english: string]>> = {
    firstContractEnabled: ['0_comision_primul_contract', 'loyalty_zero_commission_first_contract'],
    firstContractFreeDays: ['zero_comision_primele_zile', 'loyalty_zero_commission_initial_days'],
    firstContractLoanLimitRon: ['limita_credit_0_comision', 'loyalty_zero_commission_credit_limit'],
    firstContractStartDate: ['Zero_Comision_Start_Date', 'loyalty_zero_commission_start_date'],
    sosEnabled: ['SOS_0_comision', 'loyalty_sos_zero_commission_enabled'],
    sosFromDate: ['SOS_Zero_Comision_From_Date', 'loyalty_sos_zero_commission_from_date'],
    sosToDate: ['SOS_Zero_Comision_To_Date', 'loyalty_sos_zero_commission_to_date'],
};

/** A stored day (2020.03.25 or 2020-03-25) as YYYY-MM-DD. */
const parseDay = (value: string): string | null => {
    const match = /^(\d{4})[.-](\d{2})[.-](\d{2})/.exec(value.trim());
    return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
};

const parseNumber = (value: string): number | null => {
    const parsed = Number(value.trim().replace(',', '.'));
    return value.trim() !== '' && Number.isFinite(parsed) ? parsed : null;
};

const parseFlag = (value: string): boolean | null => {
    const text = value.trim();
    if (text === '1') {
        return true;
    }
    return text === '0' ? false : null;
};

const readSetting = <T>(
    stored: Readonly<Record<string, string>>,
    key: SettingKey,
    parse: (value: string) => T | null,
): T | null => {
    for (const name of SETTING_NAMES[key]) {
        const value = stored[name];
        const parsed = value === undefined ? null : parse(value);
        if (parsed !== null) {
            return parsed;
        }
    }
    return null;
};

/** The form's starting values from the configuration map; off and empty when nothing is set. */
export const readPromotionFormValues = (
    stored: Readonly<Record<string, string>>,
): ZeroCommissionPromotionFormValues => ({
    firstContractEnabled: readSetting(stored, 'firstContractEnabled', parseFlag) ?? false,
    firstContractFreeDays: readSetting(stored, 'firstContractFreeDays', parseNumber) ?? 0,
    firstContractLoanLimitRon: readSetting(stored, 'firstContractLoanLimitRon', parseNumber) ?? 0,
    firstContractStartDate: readSetting(stored, 'firstContractStartDate', parseDay),
    sosEnabled: readSetting(stored, 'sosEnabled', parseFlag) ?? false,
    sosFromDate: readSetting(stored, 'sosFromDate', parseDay),
    sosToDate: readSetting(stored, 'sosToDate', parseDay),
});

export type PromotionFormError =
    | 'firstContractDaysRequired'
    | 'firstContractLimitRequired'
    | 'sosWindowRequired'
    | 'sosWindowOrder';

/**
 * What stops the form from being saved, as translation keys under
 * `configuration.promotions.errors`. A switched-off promotion is never checked, so turning one
 * off always saves.
 */
export const validatePromotionForm = (
    values: ZeroCommissionPromotionFormValues,
): PromotionFormError[] => {
    const errors: PromotionFormError[] = [];
    if (values.firstContractEnabled && values.firstContractFreeDays <= 0) {
        errors.push('firstContractDaysRequired');
    }
    if (values.firstContractEnabled && values.firstContractLoanLimitRon <= 0) {
        errors.push('firstContractLimitRequired');
    }
    const { sosEnabled, sosFromDate, sosToDate } = values;
    if (!sosEnabled) {
        return errors;
    }
    if (sosFromDate === null || sosToDate === null) {
        errors.push('sosWindowRequired');
    } else if (sosFromDate > sosToDate) {
        errors.push('sosWindowOrder');
    }
    return errors;
};

/** A value as Delphi stores it: flags as 1/0, days as YYYY.MM.DD (its date pickers' form). */
const asStored = (value: boolean | number | string): string => {
    if (typeof value === 'boolean') {
        return value ? '1' : '0';
    }
    return typeof value === 'string' ? value.split('-').join('.') : String(value);
};

/**
 * The rows to save, under Delphi's names. An empty date is left out - the configuration API
 * ignores empty values - so it keeps whatever was stored before.
 */
export const toPromotionConfigurationRows = (
    values: ZeroCommissionPromotionFormValues,
): Array<{ denumire: string; valoare: string }> =>
    (Object.keys(SETTING_NAMES) as SettingKey[]).flatMap(key => {
        const value = values[key];
        return value === null
            ? []
            : [{ denumire: SETTING_NAMES[key][0], valoare: asStored(value) }];
    });
